import { useCallback, useEffect } from 'react';
import { useURLFilterState } from '@client/hooks/useURLFilterState';
import { useProductEnrichedData } from './useProductEnrichedData';
import { PRODUCT_FIELD_TO_ENRICHED } from '@client/config/filterFieldMappings';

/**
 * Filter keys configuration for product search
 * @type {string[]}
 */
const PRODUCT_FILTERS = [
  'product_names',
  'active_substance_names',
  'activity_sectors',
  'organic_approval',
  'cultures',
  'cultures_practices',
  'enemies',
  'commercial_packagings',
  'registration_numbers',
  'product_types',
  'activity_types',
  'allergens',
  'chemical_families',
  'pesticide_groups',
  'pesticide_categories',
  'arla_culture_groups',
  'application_types',
  'acute_toxicity',
  'long_term_effects',
  'fish_or_daphnia',
  'birds',
  'bees',
  'persistence',
  'leaching_potential',
];

/**
 * Specific hook for product filtering with URL synchronization
 * Composes useURLFilterState with enriched data for cold start support
 * @returns {Object} Filter state and methods for active product search
 */
export const useProductFilters = () => {
  const urlState = useURLFilterState(PRODUCT_FILTERS, {
    defaultPagination: { page: 1, size: 20 },
    throttleMs: 300,
    clearOnDefault: true,
  });

  const {
    enrichedData,
    isLoadingLabels,
    resetEnrichedData,
    updateEnrichedField,
    getLabelsForField,
    buildSelectionLabels,
  } = useProductEnrichedData(urlState.filters);

  // Sync enriched labels into the filter labels store for display
  useEffect(() => {
    if (isLoadingLabels || !enrichedData) return;

    Object.entries(PRODUCT_FIELD_TO_ENRICHED).forEach(([filterKey, enrichedKey]) => {
      const items = enrichedData[enrichedKey];
      if (items && items.length > 0) {
        items.forEach((item) => {
          if (item.value && item.label) {
            urlState.setFilterLabel(filterKey, item.value, item.label);
          }
        });
      }
    });
  }, [enrichedData, isLoadingLabels, urlState.setFilterLabel]);

  // Wrap resetFilters to also reset enriched data
  const resetFilters = useCallback(() => {
    urlState.resetFilters();
    resetEnrichedData();
  }, [urlState.resetFilters, resetEnrichedData]);

  return {
    ...urlState,
    resetFilters,
    enrichedData,
    isLoadingLabels,
    updateEnrichedField,
    getLabelsForField,
    buildSelectionLabels,
  };
};
